import React, { useState, useEffect, FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LoadingDots } from "../utilities/Utilities";
import { useAppContext } from "../../Contexts/AppContext";
import { z } from "zod";
import { EyeClosedIcon, EyeOpenIcon } from "@radix-ui/react-icons";
import OnBoardingShell from "../onboarding-shell/OnBoardingShell";
import { useUserContext } from "../../Contexts/UserContext";
import { useCaseContext } from "../../Contexts/CaseContext";
import { useDivisionContext } from "../../Contexts/DivisionContext";
import { useNotificationContext } from "../../Contexts/NotificationContext";
import PendingApprovalModal from "../../components/PendingApprovalModal";

const LoginFormSchema = z.object({
  email: z.string().email("Invalid email address"),
  // password: z.string().min(8, 'Password must be at least 8 characters long'),
});

const LoginForm: React.FC = () => {
  const {
    handleLogin,
    resetRegistrationSuccess,
    showPassword,
    togglePasswordVisibility,
    registrationSuccess,
  } = useAppContext();
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { fetchUserData } = useUserContext();
  const { fetchCaseData } = useCaseContext();
  const { fetchDivisionData, currentPage } = useDivisionContext();
  const { fetchNotifications } = useNotificationContext();
  const [showPendingModal, setShowPendingModal] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (registrationSuccess) {
      const timer = setTimeout(() => {
        resetRegistrationSuccess();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [registrationSuccess, resetRegistrationSuccess]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    // Validate email
    const result = LoginFormSchema.safeParse({ email });
    if (!result.success) {
      setError(result.error.errors.map((err) => err.message).join(", "));
      setIsLoading(false);
      return;
    }

    // Check for empty password
    if (password.trim() === "") {
      setError("Password is required.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch("https://backend-kyw8.onrender.com/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.status === 202) {
        navigate("/verify-email", {
          state: { email },
        });
      } else if (response.status === 404) {
        setError("User not found");
      } else if (response.status === 401) {
        setError("Incorrect username or password");
      } else if (response.status === 400) {
        const data = await response.json();
        if (data.error?.toLowerCase().includes("pending")) {
          setShowPendingModal(true);
        } else {
          setError(data.error || "Bad request");
        }
      } else if (response.ok) {
        const data = await response.json();
        const token = data.token;
        sessionStorage.setItem("token", token);

        await fetchUserData();
        await fetchCaseData();
        await fetchDivisionData(currentPage);
        await fetchNotifications();

        // Reset form on success
        setEmail("");
        setPassword("");
        setError(null);
        handleLogin();
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error("Error:", error.message);
        setError("Failed to connect to the server. Please try again later.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <PendingApprovalModal
        isOpen={showPendingModal}
        onClose={() => setShowPendingModal(false)}
      />
      <OnBoardingShell title="Welcome Back!" screenText="Login to continue">
        <div className="md:w-1/2 w-full relative flex flex-col items-center justify-center px-10 m-auto">
          <h2 className="form-header !text-start w-full mb-5">Login</h2>
          <form onSubmit={handleSubmit} className="w-full">
            <div className="mb-2">
              <label htmlFor="email" className="input-label">
                Email *
              </label>
              <input
                type="email"
                id="email"
                value={email}
                placeholder="Email"
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input"
              />
            </div>
            <div className="mb-2 relative">
              <label htmlFor="password" className="input-label">
                Password *
              </label>
              <div className="password-input-container">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  placeholder="Password"
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pr-16"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility("password")}
                  className="absolute inset-y-8 right-0 px-3"
                >
                  {showPassword ? (
                    <EyeOpenIcon className="h-8" />
                  ) : (
                    <EyeClosedIcon className="h-8" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex w-full">
              <Link
                to={"/forgot-password"}
                className="w-full font-medium text-sm text-end underline"
              >
                Forgot password?
              </Link>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="button-primary !font-semibold !text-white py-2"
            >
              {isLoading ? <LoadingDots /> : "Login"}
            </button>
            {error && <p className="text-red-500 mt-3">{error}</p>}
            <p className="paragraph-text">
              Don&apos;t have an account?{" "}
              <Link to="/register">
                <span className="font-semibold underline">Register</span>
              </Link>
            </p>
          </form>
        </div>
      </OnBoardingShell>
    </>
  );
};

export default LoginForm;
